/**
 * ScoringBreakdownCard component for marketing pages
 * Placeholder component for scoring breakdown visual showing how emails are flagged
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { XCircle } from 'lucide-react';

export interface ScoringBreakdownCardProps {
  /**
   * Risk score from 0-1 scale (displayed as percentage)
   * @example 0.82 → "82%"
   */
  score: number;
  /**
   * Top 3-5 reasons why the email was flagged
   */
  reasons: string[];
  /**
   * Optional email preview information
   */
  emailPreview?: {
    from: string;
    subject: string;
  };
  /**
   * Optional additional CSS classes
   */
  className?: string;
  /**
   * Optional unique ID for the heading element (for aria-labelledby).
   * Required when multiple ScoringBreakdownCards are rendered on the same page
   * to avoid duplicate IDs.
   * @default "scoring-breakdown-title"
   */
  headingId?: string;
}

/**
 * ScoringBreakdownCard component
 *
 * Displays a scoring breakdown for flagged emails with score, reasons, and optional email preview.
 * Used in proof/demo sections to show how Email Ferret identifies suspicious outreach.
 *
 * @param score - Risk score from 0-1 scale
 * @param reasons - Array of reason strings explaining why flagged
 * @param emailPreview - Optional email preview with from and subject
 * @param className - Optional additional CSS classes
 *
 * @example
 * ```tsx
 * <ScoringBreakdownCard
 *   score={0.82}
 *   reasons={[
 *     "Domain age < 1 year",
 *     "No previous contact",
 *     "Sales intent detected"
 *   ]}
 *   emailPreview={{
 *     from: "sales@newcompany.com",
 *     subject: "Quick question about your business"
 *   }}
 * />
 * ```
 */
export function ScoringBreakdownCard({
  score,
  reasons,
  emailPreview,
  className = '',
  headingId = 'scoring-breakdown-title',
}: ScoringBreakdownCardProps): React.ReactElement {
  const scorePercentage = Math.round(score * 100);
  const scoreColor =
    scorePercentage >= 80
      ? 'text-red-500'
      : scorePercentage >= 60
        ? 'text-orange-500'
        : 'text-yellow-500';

  return (
    <article
      className={cn('bg-card/80 backdrop-blur-sm rounded-2xl p-6 border shadow-xl', className)}
      aria-labelledby={headingId}
    >
      <div className="mb-4">
        <h3 id={headingId} className="text-lg font-semibold mb-2">
          Flagged Outreach Example
        </h3>
        {emailPreview && (
          <div className="text-sm text-muted-foreground mb-3 space-y-1">
            <div>
              <span className="font-medium">From:</span> {emailPreview.from}
            </div>
            <div>
              <span className="font-medium">Subject:</span> {emailPreview.subject}
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-medium text-muted-foreground">Risk Score:</span>
          <span className={cn('text-3xl font-bold', scoreColor)}>{scorePercentage}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className={cn('h-2 rounded-full transition-all', {
              'bg-red-500': scorePercentage >= 80,
              'bg-orange-500': scorePercentage >= 60 && scorePercentage < 80,
              'bg-yellow-500': scorePercentage < 60,
            })}
            style={{ width: `${scorePercentage}%` }}
            role="progressbar"
            aria-valuenow={scorePercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Risk score: ${scorePercentage}%`}
          />
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Why flagged:</h4>
        <ul className="space-y-2" role="list" aria-label="Reasons for flagging">
          {reasons.map((reason, index) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
